import JSZip from "jszip";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import type { AssetRecord, ExportPreset, RawRecipe } from "@focalfolio/domain-model";
import type { FileReference, WorkspaceFileSystem } from "@focalfolio/fs-contract";
import { recipeToCssFilter } from "@focalfolio/application-core";

function safeFilename(value: string): string {
  const cleaned = value.replace(/[\\/:*?"<>|\u0000-\u001f]/gu, "_").replace(/[. ]+$/gu, "").trim();
  return cleaned || "untitled";
}

function originalStem(asset: AssetRecord): string {
  return asset.extension ? asset.filename.slice(0, -(asset.extension.length + 1)) : asset.filename;
}

function extensionForMime(format: ExportPreset["format"]): string {
  if (format === "image/png") return "png";
  if (format === "image/webp") return "webp";
  return "jpg";
}

export function exportFilename(asset: AssetRecord, preset: ExportPreset, sequence: number): string {
  const value = preset.filenameTemplate
    .replaceAll("{originalName}", originalStem(asset))
    .replace(/\{sequence(?::(0+))?\}/gu, (_, zeroes: string | undefined) => String(sequence).padStart(zeroes?.length ?? 1, "0"))
    .replaceAll("{rating}", String(asset.rating));
  return `${safeFilename(value)}.${extensionForMime(preset.format)}`;
}

async function canvasForBlob(blob: Blob, maximumWidth: number, maximumHeight: number, recipe?: RawRecipe): Promise<OffscreenCanvas> {
  const bitmap = await createImageBitmap(blob);
  try {
    const scale = Math.min(1, maximumWidth / bitmap.width, maximumHeight / bitmap.height);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) throw new Error("Canvas 2D rendering is unavailable.");
    if (recipe) context.filter = recipeToCssFilter(recipe);
    if (recipe?.rotation) {
      const radians = (recipe.rotation * Math.PI) / 180;
      context.translate(width / 2, height / 2);
      context.rotate(radians);
      context.drawImage(bitmap, -width / 2, -height / 2, width, height);
    } else {
      context.drawImage(bitmap, 0, 0, width, height);
    }
    return canvas;
  } finally {
    bitmap.close();
  }
}

export async function resizeImageBlob(
  blob: Blob,
  options: { format: ExportPreset["format"]; quality: number; maxWidth: number; maxHeight: number; recipe?: RawRecipe }
): Promise<Blob> {
  const canvas = await canvasForBlob(blob, options.maxWidth, options.maxHeight, options.recipe);
  return canvas.convertToBlob({ type: options.format, quality: options.quality });
}

export async function exportAsset(input: {
  fileSystem: WorkspaceFileSystem;
  asset: AssetRecord;
  sourceBlob?: Blob;
  destinationRootId: string;
  destinationDirectory: string;
  preset: ExportPreset;
  sequence: number;
  overwrite?: boolean;
}): Promise<{ destination: FileReference; bytes: number }> {
  const source = input.sourceBlob ?? (await input.fileSystem.getFile({ rootId: input.asset.rootId, relativePath: input.asset.relativePath }));
  const output = await resizeImageBlob(source, {
    format: input.preset.format,
    quality: input.preset.quality,
    maxWidth: input.preset.maxWidth,
    maxHeight: input.preset.maxHeight,
    recipe: input.asset.adjustment
  });
  const destination: FileReference = {
    rootId: input.destinationRootId,
    relativePath: [input.destinationDirectory, exportFilename(input.asset, input.preset, input.sequence)].filter(Boolean).join("/")
  };
  await input.fileSystem.writeFile(destination, output, { overwrite: input.overwrite });
  return { destination, bytes: output.size };
}

export interface ContactSheetOptions {
  pageWidth?: number;
  pageHeight?: number;
  columns?: number;
  rows?: number;
  margin?: number;
  gap?: number;
  caption?: "filename" | "path" | "none";
  title?: string;
}

export async function createContactSheetPdf(
  items: Array<{ asset: AssetRecord; blob: Blob }>,
  options: ContactSheetOptions = {}
): Promise<Blob> {
  const document = await PDFDocument.create();
  const font = await document.embedFont(StandardFonts.Helvetica);
  const pageWidth = options.pageWidth ?? 842;
  const pageHeight = options.pageHeight ?? 595;
  const columns = Math.max(1, options.columns ?? 4);
  const rows = Math.max(1, options.rows ?? 3);
  const margin = options.margin ?? 28;
  const gap = options.gap ?? 12;
  const header = options.title ? 28 : 0;
  const captionHeight = options.caption === "none" ? 0 : 16;
  const cellWidth = (pageWidth - margin * 2 - gap * (columns - 1)) / columns;
  const cellHeight = (pageHeight - margin * 2 - header - gap * (rows - 1)) / rows;
  const perPage = columns * rows;

  for (let pageStart = 0; pageStart < items.length; pageStart += perPage) {
    const page = document.addPage([pageWidth, pageHeight]);
    if (options.title) page.drawText(options.title, { x: margin, y: pageHeight - margin + 4, size: 15, font, color: rgb(0.1, 0.1, 0.12) });
    const pageItems = items.slice(pageStart, pageStart + perPage);
    for (let index = 0; index < pageItems.length; index += 1) {
      const item = pageItems[index];
      if (!item) continue;
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = margin + column * (cellWidth + gap);
      const yTop = pageHeight - margin - header - row * (cellHeight + gap);
      const preview = await resizeImageBlob(item.blob, {
        format: "image/jpeg",
        quality: 0.82,
        maxWidth: Math.round(cellWidth * 2),
        maxHeight: Math.round((cellHeight - captionHeight) * 2),
        recipe: item.asset.adjustment
      });
      const embedded = await document.embedJpg(await preview.arrayBuffer());
      const availableHeight = cellHeight - captionHeight;
      const scale = Math.min(cellWidth / embedded.width, availableHeight / embedded.height);
      const drawWidth = embedded.width * scale;
      const drawHeight = embedded.height * scale;
      page.drawImage(embedded, {
        x: x + (cellWidth - drawWidth) / 2,
        y: yTop - drawHeight,
        width: drawWidth,
        height: drawHeight
      });
      if (options.caption !== "none") {
        const caption = options.caption === "path" ? item.asset.relativePath : item.asset.filename;
        page.drawText(caption.slice(0, 70), { x, y: yTop - availableHeight - 12, size: 8, font, color: rgb(0.18, 0.18, 0.2) });
      }
    }
  }
  document.setTitle(options.title ?? "FocalFolio Contact Sheet");
  document.setProducer("FocalFolio local output engine");
  return new Blob([new Uint8Array(await document.save())], { type: "application/pdf" });
}

function htmlEscape(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] ?? character);
}

export async function createWebGalleryZip(
  items: Array<{ asset: AssetRecord; blob: Blob }>,
  options: { title?: string; includeMetadata?: boolean } = {}
): Promise<Blob> {
  const zip = new JSZip();
  const title = options.title ?? "FocalFolio Gallery";
  const manifest: Array<Record<string, unknown>> = [];
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (!item) continue;
    const stem = `${String(index + 1).padStart(4, "0")}-${safeFilename(originalStem(item.asset))}`;
    const preview = await resizeImageBlob(item.blob, { format: "image/jpeg", quality: 0.86, maxWidth: 2200, maxHeight: 2200, recipe: item.asset.adjustment });
    const thumbnail = await resizeImageBlob(item.blob, { format: "image/jpeg", quality: 0.76, maxWidth: 480, maxHeight: 480, recipe: item.asset.adjustment });
    zip.file(`previews/${stem}.jpg`, preview);
    zip.file(`thumbs/${stem}.jpg`, thumbnail);
    manifest.push({
      filename: item.asset.filename,
      preview: `previews/${stem}.jpg`,
      thumbnail: `thumbs/${stem}.jpg`,
      ...(options.includeMetadata
        ? { title: item.asset.metadata.title, description: item.asset.metadata.description, creator: item.asset.metadata.creator, keywords: item.asset.metadata.keywords }
        : {})
    });
  }
  zip.file("manifest.json", JSON.stringify({ version: 1, title, generatedAt: new Date().toISOString(), items: manifest }, null, 2));
  zip.file(
    "styles.css",
    `:root{color-scheme:dark;background:#0a0d14;color:#eef2ff;font:16px system-ui}body{margin:0}header{padding:28px 5vw;border-bottom:1px solid #293043}main{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:18px;padding:24px 5vw}figure{margin:0;background:#141925;border-radius:14px;overflow:hidden}img{width:100%;aspect-ratio:4/3;object-fit:cover;display:block}figcaption{padding:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}a{color:inherit;text-decoration:none}`
  );
  zip.file(
    "index.html",
    `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${htmlEscape(title)}</title><link rel="stylesheet" href="styles.css"></head><body><header><h1>${htmlEscape(title)}</h1><p>Generated locally by FocalFolio.</p></header><main>${manifest
      .map((item) => `<figure><a href="${item.preview}"><img loading="lazy" src="${item.thumbnail}" alt="${htmlEscape(String(item.filename))}"></a><figcaption>${htmlEscape(String(item.filename))}</figcaption></figure>`)
      .join("")}</main></body></html>`
  );
  return zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
